import React, { useState, useEffect, useRef } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLang } from '../../context/LangContext';
import CenteredLayout from '../../layout/CenteredLayout';
import PageContainerLayout from '../../layout/PageContainerLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';

// 토스페이먼츠 테스트 클라이언트 키
const TOSS_CLIENT_KEY = 'test_ck_D5GePWvyJnrK0W0k6q8gLzN97Eoq';
const CUSTOMER_KEY = 'test_customer_' + Date.now(); // 고객 고유 키

// 다국어 텍스트
const texts = {
  ko: {
    title: '결제수단 관리',
    testMode: '🧪 테스트 모드 - 토스페이먼츠 브랜드페이 UI',
    loading: '토스페이먼츠 UI를 로딩 중...',
    error: '토스페이먼츠 UI 로딩에 실패했습니다.',
    retry: '다시 시도',
    guide: '아래에 토스페이먼츠에서 제공하는 실제 결제수단 관리 UI가 표시됩니다.',
  },
  en: {
    title: 'Payment Management',
    testMode: '🧪 Test Mode - TossPayments BrandPay UI',
    loading: 'Loading TossPayments UI...',
    error: 'Failed to load TossPayments UI.',
    retry: 'Retry',
    guide: 'The actual payment method management UI provided by TossPayments will be displayed below.',
  }
};

// 토스페이먼츠 SDK 동적 로드
const loadTossPaymentsSDK = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    // 이미 로드되어 있다면 바로 반환
    if ((window as any).PaymentWidget) {
      resolve((window as any).PaymentWidget);
      return;
    }

    // 스크립트 태그 생성
    const script = document.createElement('script');
    script.src = 'https://js.tosspayments.com/v1/payment-widget';
    script.async = true;
    
    script.onload = () => {
      // 로드 완료 후 약간의 지연을 두고 확인
      setTimeout(() => {
        if ((window as any).PaymentWidget) {
          resolve((window as any).PaymentWidget);
        } else {
          reject(new Error('PaymentWidget이 로드되지 않았습니다.'));
        }
      }, 100);
    };
    
    script.onerror = () => {
      reject(new Error('토스페이먼츠 SDK 로드에 실패했습니다.'));
    };
    
    document.head.appendChild(script);
  });
};

function PaymentManagement() {
  const navigate = useNavigate();
  const { isLoggedIn } = useUserAuth();
  const { lang } = useLang();
  const t = texts[lang];
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('초기화 대기 중...');
  const paymentWidgetRef = useRef<any>(null);
  const widgetContainerRef = useRef<HTMLDivElement>(null);

  // 권한 확인
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
  }, [isLoggedIn, navigate]);

  // 토스페이먼츠 Payment Widget 초기화
  useEffect(() => {
    const initializeTossPayments = async () => {
      if (!isLoggedIn) return;

      try {
        setIsLoading(true);
        setError(null);
        setDebugInfo('토스페이먼츠 SDK 로딩 중...');

        devLog('토스페이먼츠 SDK 로딩 시작...');
        
        // 토스페이먼츠 SDK 로드
        const PaymentWidget = await loadTossPaymentsSDK();
        devLog('토스페이먼츠 SDK 로드 완료:', PaymentWidget);
        setDebugInfo('SDK 로드 완료, 위젯 생성 중...');
        
        // Payment Widget 인스턴스 생성
        const paymentWidget = PaymentWidget(TOSS_CLIENT_KEY, CUSTOMER_KEY);
        devLog('Payment Widget 인스턴스 생성 완료:', paymentWidget);
        setDebugInfo('위젯 인스턴스 생성 완료, 렌더링 준비 중...');
        
        paymentWidgetRef.current = paymentWidget;

        // 컨테이너가 존재하는지 확인
        if (!widgetContainerRef.current) {
          throw new Error('위젯 컨테이너를 찾을 수 없습니다.');
        }

        devLog('위젯 컨테이너 확인 완료:', widgetContainerRef.current);
        setDebugInfo('컨테이너 확인 완료, 위젯 렌더링 시작...');

        // 기존 위젯 정리
        widgetContainerRef.current.innerHTML = '';
        
        // 결제수단 위젯 렌더링
        devLog('결제수단 위젯 렌더링 시작...');
        
        try {
          // 토스페이먼츠 공식 방식: 결제창 위젯 렌더링
          devLog('기본 렌더링 시도...');
          setDebugInfo('결제 위젯 기본 렌더링 시도 중...');
          
          const paymentMethodsWidget = paymentWidget.renderPaymentMethods(
            '#payment-methods-widget',
            { value: 15000, currency: 'KRW' }
          );
          
          devLog('결제수단 위젯 렌더링 성공:', paymentMethodsWidget);
          setDebugInfo('결제 위젯 렌더링 성공!');
          
        } catch (renderError) {
          devLog('기본 렌더링 실패, 브랜드페이 방식 시도:', renderError);
          setDebugInfo('기본 렌더링 실패, 브랜드페이 시도 중...');
          
          try {
            // 브랜드페이 방식 시도
            const brandpay = paymentWidget.brandpay({
              redirectUrl: window.location.origin + '/payment/success',
              failUrl: window.location.origin + '/payment/fail',
            });
            
            const brandpayWidget = brandpay.renderPaymentMethodEdit('#payment-methods-widget');
            devLog('브랜드페이 위젯 렌더링 성공:', brandpayWidget);
            setDebugInfo('브랜드페이 위젯 렌더링 성공!');
            
          } catch (brandpayError) {
            devLog('브랜드페이도 실패, 약관만 렌더링:', brandpayError);
            setDebugInfo('브랜드페이 실패, 약관 위젯 시도 중...');
            
            try {
              // 약관 위젯만 렌더링
              const agreementWidget = paymentWidget.renderAgreement('#payment-methods-widget');
              devLog('약관 위젯 렌더링 성공:', agreementWidget);
              setDebugInfo('약관 위젯 렌더링 성공!');
              
            } catch (agreementError) {
              devError('모든 렌더링 방법 실패:', agreementError);
              setDebugInfo('모든 위젯 렌더링 실패');
              
              // 마지막 시도: 수동으로 테스트 UI 생성
              if (widgetContainerRef.current) {
                widgetContainerRef.current.innerHTML = `
                  <div style="padding: 20px; text-align: center; border: 2px dashed #e5e7eb; border-radius: 8px;">
                    <h3 style="margin: 0 0 10px 0; color: #374151;">토스페이먼츠 테스트 환경</h3>
                    <p style="margin: 0 0 15px 0; color: #6b7280; font-size: 14px;">
                      현재 테스트 키로 실제 결제수단 관리 UI를 로드할 수 없습니다.
                    </p>
                    <p style="margin: 0; color: #9ca3af; font-size: 12px;">
                      실제 운영 환경에서는 토스페이먼츠 위젯이 정상적으로 표시됩니다.
                    </p>
                  </div>
                `;
                setDebugInfo('테스트 UI로 대체됨');
              }
            }
          }
        }
        
        devLog('결제수단 위젯 렌더링 완료');
        setIsLoading(false);
        
      } catch (err) {
        devError('토스페이먼츠 초기화 실패:', err);
        setError(err instanceof Error ? err.message : '토스페이먼츠 UI 로딩에 실패했습니다.');
        setDebugInfo('오류: ' + (err instanceof Error ? err.message : '알 수 없는 오류'));
        setIsLoading(false);
      }
    };

    const currentContainer = widgetContainerRef.current;
    
    // 약간의 지연을 두고 초기화 (DOM이 완전히 준비될 때까지)
    const timer = setTimeout(() => {
      initializeTossPayments();
    }, 100);

    // 컴포넌트 언마운트 시 정리
    return () => {
      clearTimeout(timer);
      if (currentContainer) {
        currentContainer.innerHTML = '';
      }
    };
  }, [isLoggedIn]);

  // 재시도 함수
  const handleRetry = () => {
    window.location.reload();
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <CenteredLayout>
      <ProjectAppbar 
        currentPage="home"
        title={t.title}
        showBackButton={true}
        onBack={() => navigate(-1)}
      />
      <PageContainerLayout>
        <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
          {/* 테스트 모드 알림 */}
          <div style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            color: '#92400e',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {t.testMode}
          </div>

          {/* 안내 메시지 */}
          <div style={{
            background: '#f0f9ff',
            border: '1px solid #0ea5e9',
            color: '#0c4a6e',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {t.guide}
          </div>

          {/* 디버그 정보 표시 */}
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '20px',
            fontSize: '12px',
            color: '#64748b',
            fontFamily: 'monospace'
          }}>
            <strong>디버그 정보:</strong> {debugInfo}
          </div>

          {/* 로딩 상태 */}
          {isLoading && (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px', 
              color: '#6b7280' 
            }}>
              <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
              <div>{t.loading}</div>
            </div>
          )}

          {/* 에러 상태 */}
          {error && !isLoading && (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px', 
              color: '#ef4444' 
            }}>
              <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
              <div style={{ marginBottom: '16px' }}>{error}</div>
              <button 
                onClick={handleRetry}
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer'
                }}
              >
                {t.retry}
              </button>
            </div>
          )}

          {/* 토스페이먼츠 Payment Widget UI */}
          <div 
            id="payment-methods-widget" 
            ref={widgetContainerRef}
            style={{ 
              minHeight: '400px',
              display: error ? 'none' : 'block',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              background: 'white',
              position: 'relative'
            }}
          >
            {/* 로딩 중일 때 보여줄 플레이스홀더 */}
            {isLoading && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                color: '#6b7280'
              }}>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>⏳</div>
                <div>위젯 로딩 중...</div>
              </div>
            )}
            
            {/* SDK가 로드되지 않았을 때의 대체 메시지 */}
            {!isLoading && !error && (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#9ca3af',
                fontSize: '14px'
              }}>
                토스페이먼츠 결제수단 관리 UI 영역
                <br />
                <small>SDK 로딩 및 위젯 초기화 중입니다...</small>
              </div>
            )}
          </div>
        </div>
      </PageContainerLayout>
    </CenteredLayout>
  );
}

export default PaymentManagement;
